// RiskHistoryTab — audit log tab showing chronological history entries.
// Each entry shows who changed what, when, the change notes, and a computed diff
// of what actually changed compared to the previous snapshot.

import { useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  risksApi,
  lifecycleStateLabels,
  lifecycleStateColors,
  type RiskHistoryEntry,
  type HistoryImpactAssessment,
} from '@/lib/api/risks'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { Clock, User, FileText, ArrowRight } from 'lucide-react'

export function RiskHistoryTab() {
  const { slug } = useTenant()
  const { riskId } = useParams({ strict: false }) as { riskId: string }
  const api = useApi()
  const riskApi = risksApi(api)

  const {
    data: history,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.risks.history(slug, riskId),
    queryFn: () => riskApi.history(riskId),
  })

  if (isLoading) return <TableSkeleton rows={5} />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load audit history"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  if (!history || history.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-muted-foreground">
        <p className="text-sm">No history entries yet.</p>
      </div>
    )
  }

  return (
    <div className="max-w-4xl p-6">
      <div className="relative">
        <div className="absolute bottom-0 left-4 top-0 w-px bg-border" />
        <div className="space-y-0">
          {history.map((entry, index) => (
            <HistoryEntryRow
              key={entry.id}
              entry={entry}
              isFirst={index === 0}
              previousEntry={index < history.length - 1 ? history[index + 1] : undefined}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

// --- Change detection ---

interface FieldChange {
  field: string
  from: string | null
  to: string | null
}

function computeChanges(
  entry: RiskHistoryEntry,
  prev: RiskHistoryEntry | undefined,
): FieldChange[] {
  if (!prev) return [{ field: 'Created', from: null, to: null }]

  const changes: FieldChange[] = []

  if (prev.lifecycle_state !== entry.lifecycle_state) {
    changes.push({
      field: 'Lifecycle State',
      from: lifecycleStateLabels[prev.lifecycle_state] ?? prev.lifecycle_state,
      to: lifecycleStateLabels[entry.lifecycle_state] ?? entry.lifecycle_state,
    })
  }

  if (prev.taxonomy_node_id !== entry.taxonomy_node_id) {
    changes.push({
      field: 'Taxonomy Category',
      from: `${prev.taxonomy_node_name} (${prev.taxonomy_node_path})`,
      to: `${entry.taxonomy_node_name} (${entry.taxonomy_node_path})`,
    })
  }

  // Likelihood changes
  const likelihoodFields: {
    field: string
    prevId: string | null | undefined
    currId: string | null | undefined
    prevName: string | null | undefined
    currName: string | null | undefined
  }[] = [
    {
      field: 'Inherent Likelihood',
      prevId: prev.inherent_likelihood_level_id,
      currId: entry.inherent_likelihood_level_id,
      prevName: prev.inherent_likelihood_name,
      currName: entry.inherent_likelihood_name,
    },
    {
      field: 'Residual Likelihood',
      prevId: prev.residual_likelihood_level_id,
      currId: entry.residual_likelihood_level_id,
      prevName: prev.residual_likelihood_name,
      currName: entry.residual_likelihood_name,
    },
    {
      field: 'Target Likelihood',
      prevId: prev.target_likelihood_level_id,
      currId: entry.target_likelihood_level_id,
      prevName: prev.target_likelihood_name,
      currName: entry.target_likelihood_name,
    },
  ]

  for (const lf of likelihoodFields) {
    if (lf.prevId !== lf.currId) {
      changes.push({
        field: lf.field,
        from: lf.prevName ?? (lf.prevId ? 'Set' : 'Not set'),
        to: lf.currName ?? (lf.currId ? 'Set' : 'Not set'),
      })
    }
  }

  // Impact assessment changes — only compare when both entries have impact data.
  // An empty array means "not recorded in this snapshot", not "cleared".
  if (prev.impact_assessments.length > 0 && entry.impact_assessments.length > 0) {
    const prevImpacts = buildImpactMap(prev.impact_assessments)
    const currImpacts = buildImpactMap(entry.impact_assessments)
    const allImpactKeys = new Set([...Object.keys(prevImpacts), ...Object.keys(currImpacts)])

    for (const key of allImpactKeys) {
      const p = prevImpacts[key]
      const c = currImpacts[key]
      if (p?.impact_level_id !== c?.impact_level_id) {
        const label = `${capitalize(key.split(':')[0] ?? '')} ${c?.impact_table_name ?? p?.impact_table_name ?? 'Impact'}`
        changes.push({
          field: label,
          from: p ? `${p.impact_level_name} (${p.level_number})` : 'Not set',
          to: c ? `${c.impact_level_name} (${c.level_number})` : 'Not set',
        })
      }
    }
  }

  // If no field-level changes detected but entry exists, mark as snapshot
  if (changes.length === 0) {
    changes.push({ field: 'Snapshot recorded', from: null, to: null })
  }

  return changes
}

function buildImpactMap(
  assessments: HistoryImpactAssessment[],
): Record<string, HistoryImpactAssessment> {
  const map: Record<string, HistoryImpactAssessment> = {}
  for (const a of assessments) {
    map[`${a.score_type}:${a.impact_table_id}`] = a
  }
  return map
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// --- Individual history entry ---

function HistoryEntryRow({
  entry,
  isFirst,
  previousEntry,
}: {
  entry: RiskHistoryEntry
  isFirst: boolean
  previousEntry?: RiskHistoryEntry
}) {
  const changes = computeChanges(entry, previousEntry)
  const isCreation = !previousEntry

  return (
    <div className="relative pb-6 pl-10">
      <div
        className={cn(
          'absolute left-2.5 h-3 w-3 rounded-full border-2 bg-background',
          isFirst ? 'border-primary' : 'border-muted-foreground/40',
        )}
      />

      <div className="space-y-3 rounded-lg border bg-card p-4">
        {/* Header: who and when */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <User className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-medium">{entry.changed_by_name}</span>
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            <time dateTime={entry.changed_at}>{new Date(entry.changed_at).toLocaleString()}</time>
          </div>
        </div>

        {/* Change notes */}
        {entry.change_notes && (
          <div className="flex items-start gap-2 text-sm">
            <FileText className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
            <p className="text-muted-foreground">{entry.change_notes}</p>
          </div>
        )}

        {/* Current state badge */}
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={cn('text-xs', lifecycleStateColors[entry.lifecycle_state])}
          >
            {lifecycleStateLabels[entry.lifecycle_state]}
          </Badge>
          {isCreation && <span className="text-xs text-muted-foreground">(created)</span>}
        </div>

        {/* Changes summary */}
        {!isCreation && changes.length > 0 && (
          <div className="space-y-1 border-t pt-3">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Changes
            </p>
            {changes.map((c, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="w-36 shrink-0 font-medium text-muted-foreground">{c.field}</span>
                {c.from !== null && c.to !== null ? (
                  <>
                    <span className="rounded bg-red-50 px-1.5 py-0.5 text-red-700 line-through">
                      {c.from}
                    </span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <span className="rounded bg-green-50 px-1.5 py-0.5 text-green-700">{c.to}</span>
                  </>
                ) : (
                  <span className="text-muted-foreground">{c.field}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
